/**
 * Centralized API Configuration
 *
 * Single source of truth for API URLs and configuration.
 * This eliminates the need for services to duplicate URL logic.
 *
 * IMPORTANT:
 * - Web: Webpack DefinePlugin replaces process.env.REACT_APP_* at build time
 * - iOS/Android: named imports from `@env` (react-native-dotenv / Babel) inject values at build time
 */

import {
  REACT_APP_API_BASE_URL,
  REACT_APP_API_BASE_URL_ANDROID,
  REACT_APP_API_BASE_URL_ANDROID_DEVICE,
  REACT_APP_API_BASE_URL_IOS,
  REACT_APP_API_BASE_URL_IOS_DEVICE,
  REACT_APP_API_TOKEN,
  REACT_APP_BASE_URL,
} from '@env';
import { Platform } from 'react-native';
/**
 * Reading settings from @env (the ".env file" bridge on phones)
 * ----------------------------------------------------------------
 * Think of @env as a vending machine: each button is ONE setting (one variable name).
 *
 * - On a REAL phone build or a normal dev run, Babel turns each `import { FOO } from '@env'`
 *   into the actual string from your .env file. That is how iOS/Android learn the API URL.
 *
 * - We used to write `import * as Env from '@env'` ("give me the whole vending machine at once").
 *   That is shorter to type, but the web build tool (react-native-dotenv) says NO — it only
 *   allows picking named buttons, not grabbing everything in one wildcard import. Webpack then
 *   failed the pipeline with "Wildcard import is not supported."
 *
 * - So: we list every setting we need, by name, below. Boring, but every platform agrees on it.
 *
 * - In JEST TESTS only, we do NOT trust those imports — tests swap values at runtime.
 *   `readEnv()` therefore looks up keys on `jest.requireMock('@env')` when NODE_ENV is "test",
 *   so `src/__mocks__/@env.js` keeps working when tests change a value mid-suite.
 */
import DeviceInfo from 'react-native-device-info';

/** Snapshot of the native @env values so `readEnv` can look them up with a closed key set. */
const nativeEnvFromModule = {
  REACT_APP_API_BASE_URL,
  REACT_APP_API_BASE_URL_ANDROID,
  REACT_APP_API_BASE_URL_ANDROID_DEVICE,
  REACT_APP_API_BASE_URL_IOS,
  REACT_APP_API_BASE_URL_IOS_DEVICE,
  REACT_APP_API_TOKEN,
  REACT_APP_BASE_URL,
} as const;

type NativeEnvKey = keyof typeof nativeEnvFromModule;

/**
 * One helper to read a string from @env.
 * - Normal / production code path: use the imported values (see `nativeEnvFromModule`).
 * - Test path: use the Jest manual mock so tests can change values without rebuilding.
 */
function readEnv(key: NativeEnvKey): string {
  let v: string | undefined;
  if (process.env.NODE_ENV === 'test') {
    const mock = jest.requireMock('@env') as Record<
      NativeEnvKey,
      string | undefined
    >;
    // Key is NativeEnvKey (closed set), not user-controlled input.
    // eslint-disable-next-line security/detect-object-injection
    v = mock[key];
  } else {
    // eslint-disable-next-line security/detect-object-injection
    v = nativeEnvFromModule[key];
  }
  return typeof v === 'string' ? v : '';
}

/**
 * Detect if running on a physical device or simulator/emulator
 *
 * @returns true if running on physical device, false if simulator/emulator
 */
function isPhysicalDevice(): boolean {
  try {
    // Check if DeviceInfo is available
    if (!DeviceInfo || !DeviceInfo.isEmulatorSync) {
      if (__DEV__) {
        console.warn(
          '⚠️ DeviceInfo not available - install react-native-device-info',
        );
      }
      return false;
    }

    const result = DeviceInfo.isEmulatorSync();
    if (__DEV__) {
      console.log('📱 Device detection - isEmulator:', result);
    }
    return !result; // Invert: isEmulator=false means physical device
  } catch (error) {
    if (__DEV__) {
      console.warn('⚠️ Device detection failed, assuming simulator:', error);
    }
    return false;
  }
}

/**
 * True when the web app is served by webpack-dev-server on localhost (`yarn web`).
 * Matches localhost, 127.0.0.1, and IPv6 loopback on any dev port — parallel worktrees use 3000/3002/3004.
 */
export function isWebpackDevLocalWebOrigin(): boolean {
  if (typeof window === 'undefined') return false;
  if (!__DEV__) return false;
  const { hostname, port } = window.location;
  if (!port) return false;
  return (
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
  );
}

/**
 * Get the appropriate API base URL for the current platform
 *
 * Strategy:
 * - Web: Uses process.env (webpack DefinePlugin)
 * - iOS Simulator: localhost (connects to Mac's localhost)
 * - iOS Physical Device: Mac's local network IP
 * - Android Emulator: 10.0.2.2 (special alias for host machine)
 * - Android Physical Device: Mac's local network IP
 *
 * Environment Variables:
 * - REACT_APP_API_BASE_URL_IOS: Used for iOS simulator
 * - REACT_APP_API_BASE_URL_IOS_DEVICE: Used for physical iOS devices
 * - REACT_APP_API_BASE_URL_ANDROID: Used for Android emulator
 * - REACT_APP_API_BASE_URL_ANDROID_DEVICE: Used for physical Android devices
 */
function getApiBaseUrl(): string {
  if (Platform.OS === 'web') {
    // Webpack dev server (yarn web) proxies /api → backend on :3001. Same-origin requests
    // avoid CORS and avoid broken REACT_APP_* URLs when testing locally.
    if (isWebpackDevLocalWebOrigin()) {
      return '';
    }
    // Web uses webpack DefinePlugin
    return (
      process.env.REACT_APP_API_BASE_URL_WEB ||
      process.env.REACT_APP_API_BASE_URL ||
      ''
    );
  }

  // iOS/Android: base URLs from @env (readEnv uses the Jest @env mock when NODE_ENV is test)
  if (Platform.OS === 'ios') {
    const isDevice = isPhysicalDevice();
    const deviceUrl = readEnv('REACT_APP_API_BASE_URL_IOS_DEVICE');
    const simulatorUrl = readEnv('REACT_APP_API_BASE_URL_IOS');
    const fallbackUrl = readEnv('REACT_APP_API_BASE_URL');

    if (__DEV__) {
      console.log('🔍 iOS Device Detection:', {
        isPhysicalDevice: isDevice,
        deviceUrl: deviceUrl || '(not set)',
        simulatorUrl: simulatorUrl || '(not set)',
        fallbackUrl: fallbackUrl || '(not set)',
      });
    }

    if (isDevice && deviceUrl) {
      if (__DEV__) {
        console.log(
          '📱 iOS Physical Device detected - using device-specific URL:',
          deviceUrl,
        );
      }
      return deviceUrl;
    }

    const finalUrl = simulatorUrl || fallbackUrl || '';
    if (__DEV__) {
      console.log('📱 iOS Simulator detected - using simulator URL:', finalUrl);
    }
    return finalUrl;
  }

  if (Platform.OS === 'android') {
    const isDevice = isPhysicalDevice();
    const deviceUrl = readEnv('REACT_APP_API_BASE_URL_ANDROID_DEVICE');
    const emulatorUrl = readEnv('REACT_APP_API_BASE_URL_ANDROID');
    const fallbackUrl = readEnv('REACT_APP_API_BASE_URL');

    if (isDevice && deviceUrl) {
      if (__DEV__) {
        console.log(
          '📱 Android Physical Device detected - using device-specific URL',
        );
      }
      return deviceUrl;
    }

    if (__DEV__) {
      console.log('📱 Android Emulator detected - using emulator URL');
    }
    return emulatorUrl || fallbackUrl || '';
  }

  // Fallback for other platforms
  return readEnv('REACT_APP_API_BASE_URL');
}

/**
 * Get the web application base URL (used for link sharing)
 */
function getWebBaseUrl(): string {
  if (Platform.OS === 'web') {
    return process.env.REACT_APP_BASE_URL || '';
  }
  return readEnv('REACT_APP_BASE_URL');
}

/**
 * Get API Configuration
 *
 * Returns configuration object with environment-specific values.
 * Must be a function to avoid module-level access to process.env.
 *
 * Best Practice: Never create module-level constants that reference process.env
 * because webpack DefinePlugin doesn't create a process object - it only replaces
 * individual process.env.VARIABLE references with string values.
 */
export function getApiConfig() {
  return {
    /**
     * Base URL for API requests (platform-specific)
     * Examples:
     * - Web: https://itsapiqa.vertiv.com
     * - Android: http://10.0.2.2:3001 (emulator) or https://itsapiqa.vertiv.com (device)
     * - iOS: http://localhost:3001 (simulator) or https://itsapiqa.vertiv.com (device)
     */
    BASE_URL: getApiBaseUrl(),

    /**
     * Base URL for the web application (used for generating shareable links)
     * Example: https://itsqa.vertiv.com
     */
    WEB_BASE_URL: getWebBaseUrl(),

    /**
     * API request timeout in milliseconds
     */
    TIMEOUT: 5000,

    /**
     * API token for protected environments (optional)
     */
    API_TOKEN:
      Platform.OS === 'web'
        ? process.env.REACT_APP_API_TOKEN
        : readEnv('REACT_APP_API_TOKEN') || undefined,
  } as const;
}

/**
 * Helper function to build a full API URL
 * @param path API endpoint path (e.g., '/api/requests')
 * @returns Full URL (e.g., 'https://itsapiqa.vertiv.com/api/requests')
 */
export function buildApiUrl(path: string): string {
  const baseUrl = getApiConfig().BASE_URL;

  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // Remove trailing slash from base URL if present
  const normalizedBaseUrl = baseUrl.endsWith('/')
    ? baseUrl.slice(0, -1)
    : baseUrl;

  return `${normalizedBaseUrl}${normalizedPath}`;
}

/**
 * Helper function to build a web app URL (for link sharing)
 * @param path Web app path (e.g., '/request-detail?id=123')
 * @returns Full URL (e.g., 'https://itsqa.vertiv.com/request-detail?id=123')
 */
export function buildWebUrl(path: string): string {
  const baseUrl = getApiConfig().WEB_BASE_URL;

  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // Remove trailing slash from base URL if present
  const normalizedBaseUrl = baseUrl.endsWith('/')
    ? baseUrl.slice(0, -1)
    : baseUrl;

  return `${normalizedBaseUrl}${normalizedPath}`;
}

/**
 * Log API configuration (for debugging)
 * Only logs in development mode
 */
export function logApiConfig(): void {
  if (__DEV__) {
    const config = getApiConfig();
    console.log('🔧 API Configuration:');
    console.log(`  Platform: ${Platform.OS}`);
    console.log(`  API Base URL: ${config.BASE_URL}`);
    console.log(`  Web Base URL: ${config.WEB_BASE_URL}`);
    console.log(`  Timeout: ${config.TIMEOUT}ms`);
    console.log(`  API Token: ${config.API_TOKEN ? '***SET***' : 'not set'}`);
  }
}
